/* CreateSpaceModal.tsx generated by @compiled/babel-plugin v0.37.1 */
import { ax, ix } from "@compiled/react/runtime";
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._bfhk1j28%7Bbackground-color%3Atransparent%7D");
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._bozgu2gc%7Bpadding-inline-start%3Avar(--ds-space-100%2C8px)%7D");
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._1ul9idpf%7Bmin-width%3A0%7D");
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._i0dlf1ug%7Bflex-basis%3A0%25%7D");
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._1o9zkb7n%7Bflex-shrink%3A1%7D");
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._16jlkb7n%7Bflex-grow%3A1%7D");
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._1rjc12x7%7Bpadding-block%3Avar(--ds-space-075%2C6px)%7D");
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import Button, { IconButton } from '@atlaskit/button/new';
import type { EmojiId, EmojiProvider } from '@atlaskit/emoji';
import { ResourcedEmoji } from '@atlaskit/emoji/element';
import { EmojiPicker } from '@atlaskit/emoji/picker';
import Form, { Field, RequiredAsterisk } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import AddIcon from '@atlaskit/icon/core/add';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';
import { LinkPicker, type LinkPickerProps } from '@atlaskit/link-picker';
import { SmartCardProvider } from '@atlaskit/link-provider';
import ModalDialog, { ModalBody, ModalFooter, ModalHeader } from '@atlaskit/modal-dialog';
import { Popup } from '@atlaskit/popup';
import { Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { type AtlassianLinkPickerPluginConfig, Scope, useAtlassianPlugins } from '@atlassian/link-picker-atlassian-plugin';
import { createEmojiProviderWithUpload as createEmojiProvider } from '../../common/utils/EmojiProviderUtils';
import type { CreateSpaceResponse } from '../../controllers/space-store/types';
import { useAddConversationToSpace } from '../../services/useAddConversationToSpace/useAddConversationToSpace';
import { useCreateSpace } from '../../services/useCreateSpace/useCreateSpace';
import messages from './messages';
import { SmartLinkSourceRow } from './SmartLinkSourceRow';
import { type SpaceSource, type SpaceSourceFormValue, toSpaceSource } from './types';
function useSpaceSourcePlugins(cloudId: string) {
  const configs = useMemo<AtlassianLinkPickerPluginConfig[]>(() => [{
    tabConfig: {
      tabKey: 'confluence',
      tabTitle: 'Confluence'
    },
    products: ['confluence'],
    scope: Scope.ConfluenceContentType,
    cloudId
  }, {
    tabConfig: {
      tabKey: 'jira',
      tabTitle: 'Jira'
    },
    products: ['jira'],
    scope: Scope.JiraIssueDashboardBoardProjectFilter,
    cloudId
  }, {
    tabConfig: {
      tabKey: 'atlas',
      tabTitle: 'Atlas'
    },
    products: ['townsquare'],
    scope: Scope.TownsquareProject,
    cloudId
  }, {
    tabConfig: {
      tabKey: 'loom',
      tabTitle: 'Loom'
    },
    products: ['loom'],
    scope: Scope.Loom,
    cloudId,
    disableActivityClient: true
  }], [cloudId]);
  return useAtlassianPlugins(configs);
}
const styles = {
  sourceRow: "_1rjc12x7",
  sourceRowLast: "_1rjc12x7",
  sourceTitleRow: "_16jlkb7n _1o9zkb7n _i0dlf1ug _1ul9idpf",
  emojiPickerTrigger: "_bozgu2gc _bfhk1j28"
};
export type CreateSpacePayload = {
  name: string;
  about: string;
  emoji?: EmojiId;
  sources?: SpaceSource[];
};
export type CreateSpaceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  cloudId: string;
  emojiProvider?: Promise<EmojiProvider>;
  /** When set, source rows show knowledge app icons (same resolver as chat citations). */
  assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
  /** Called after BE create succeeds. */
  /** Called after create + (optional) initial-conversation attach. Includes the
   * attached conversation id so callers can update the store optimistically. */
  onCreated?: (space: CreateSpaceResponse, emojiId?: string, attachedConversationId?: string) => void;
  /** Called after validation passes, before the modal closes and form resets (local callback, no BE call) */
  onCreate?: (payload: CreateSpacePayload) => void;
  /** Pre-populate the space name field */
  initialName?: string;
  /** Pre-populate the sources list */
  initialSources?: SpaceSource[];
  /** When set, this conversation will automatically be linked to the new space on creation */
  initialConversationId?: string;
};
type CreateSpaceFormValues = {
  name: string;
  about: string;
  // Form-only shape: carries `displayText` for the row label override. Stripped
  // to plain `SpaceSource[]` before being handed to BE callers / consumers.
  sources: SpaceSourceFormValue[];
};
const CREATE_SPACE_FORM_ID = 'create-space-form';
type SpaceSourcesLayoutProps = {
  sources: SpaceSourceFormValue[];
  onRemove: (ari: string) => void;
  formatMessage: IntlShape['formatMessage'];
  addSourcesTrigger: React.ReactNode;
};
const SpaceSourcesLayout = ({
  sources,
  onRemove,
  formatMessage,
  addSourcesTrigger
}: SpaceSourcesLayoutProps): React.JSX.Element => <Stack space="space.100">
		<Stack space="space.0">
			{sources.map((source, index) => <Flex key={source.ari} direction="row" justifyContent="space-between" alignItems="center" xcss={index < sources.length - 1 ? styles.sourceRow : styles.sourceRowLast}>
					<Flex direction="row" alignItems="center" gap="space.100" xcss={styles.sourceTitleRow}>
						<SmartLinkSourceRow url={source.url} fallback={source.name ?? ''} displayTextOverride={source.displayText} />
					</Flex>
					<IconButton icon={DeleteIcon} appearance="subtle" spacing="compact" label={formatMessage(messages.createSpaceRemoveSource, {
        name: source.name
      })} onClick={() => onRemove(source.ari)} testId={`create-space-remove-source-${source.ari}`} />
				</Flex>)}
		</Stack>
		<Flex>{addSourcesTrigger}</Flex>
	</Stack>;
type SpaceSourcesFieldBodyProps = {
  fieldProps: {
    value: unknown;
    onChange: (next: SpaceSourceFormValue[]) => void;
  };
  formatMessage: IntlShape['formatMessage'];
  plugins: Parameters<typeof LinkPicker>[0]['plugins'];
  assistanceServiceParams?: CreateSpaceModalProps['assistanceServiceParams'];
};
const SpaceSourcesFieldBody = ({
  fieldProps,
  formatMessage,
  plugins
}: SpaceSourcesFieldBodyProps): React.JSX.Element => {
  const [isPickerOpen, setPickerOpen] = useState(false);
  const sources = (fieldProps.value as SpaceSourceFormValue[] | undefined) ?? [];

  // `fieldProps` is a new object each render from Form; keep a ref so the link-picker callback stays stable.
  const fieldPropsRef = useRef(fieldProps);
  fieldPropsRef.current = fieldProps;
  const handleSubmit: LinkPickerProps['onSubmit'] = useCallback(({
    url,
    displayText,
    title
  }) => {
    const {
      onChange,
      value
    } = fieldPropsRef.current;
    const current = (value as SpaceSourceFormValue[] | undefined) ?? [];
    // Treat `displayText` as a user override only when it differs from the
    // link picker's pre-filled `title`; otherwise keep using the live SmartLink title.
    const trimmedDisplayText = displayText?.trim() || undefined;
    const resolvedTitle = title?.trim() || undefined;
    const userDisplayText = trimmedDisplayText && trimmedDisplayText !== resolvedTitle ? trimmedDisplayText : undefined;
    onChange([...current, ({
      ari: url,
      url,
      name: trimmedDisplayText ?? resolvedTitle ?? url,
      displayText: userDisplayText,
      sourceType: detectSourceType(url),
      conversationIds: []
    } satisfies SpaceSourceFormValue)]);
    setPickerOpen(false);
  }, []);
  const addSourcesTrigger = <Popup isOpen={isPickerOpen} onClose={() => setPickerOpen(false)} placement="bottom-start" zIndex={510} content={() => <LinkPicker plugins={plugins} onSubmit={handleSubmit} onCancel={() => setPickerOpen(false)} adaptiveHeight recentSearchListSize={3} />} trigger={triggerProps => <Button {...triggerProps} appearance="default" iconBefore={AddIcon} type="button" onClick={() => setPickerOpen(prev => !prev)} testId="create-space-add-sources">
					{formatMessage(messages.createSpaceAddSources)}
				</Button>} />;
  return <SpaceSourcesLayout sources={sources} onRemove={ari => fieldProps.onChange(sources.filter(s => s.ari !== ari))} formatMessage={formatMessage} addSourcesTrigger={addSourcesTrigger} />;
};
function detectSourceType(url: string): SpaceSource['sourceType'] {
  if (/confluence/i.test(url)) {
    return 'confluence';
  }
  if (/jira/i.test(url)) {
    return 'jira';
  }
  return 'confluence';
}

// Only mounted when isOpen=true; prevents useSpaceSourcePlugins from running
// before @atlassian/link-picker-atlassian-plugin is fully initialised ("Scope is undefined" crash).
const CreateSpaceModalContent = ({
  onClose,
  cloudId,
  emojiProvider,
  assistanceServiceParams,
  onCreate,
  onCreated,
  initialName,
  initialSources,
  initialConversationId
}: Omit<CreateSpaceModalProps, 'isOpen'>): React.JSX.Element => {
  const {
    formatMessage
  } = useIntl();
  const defaultEmojiProvider = useMemo(() => createEmojiProvider(cloudId), [cloudId]);
  const resolvedEmojiProvider = emojiProvider ?? defaultEmojiProvider;
  const [isEmojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [selectedEmoji, setSelectedEmoji] = useState<EmojiId | undefined>(undefined);
  const plugins = useSpaceSourcePlugins(cloudId);
  const nameRequiredMessage = useMemo(() => formatMessage(messages.createSpaceNameRequired), [formatMessage]);
  const handleEmojiSelection = useCallback((emojiId: EmojiId) => {
    setSelectedEmoji(emojiId);
    setEmojiPickerOpen(false);
  }, []);
  const reset = useCallback(() => {
    setEmojiPickerOpen(false);
    setSelectedEmoji(undefined);
  }, []);
  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);
  const {
    addConversation
  } = useAddConversationToSpace({
    cloudId,
    product: assistanceServiceParams?.product ?? 'rovo',
    experienceId: assistanceServiceParams?.experienceId ?? 'ai-mate'
  });
  const handleCreated = useCallback(async (space: CreateSpaceResponse) => {
    let attachedConversationId: string | undefined;
    // If a conversation id was provided, attach it to the newly created space
    if (initialConversationId && space?.id) {
      try {
        await addConversation({
          spaceId: space.id,
          conversationId: initialConversationId
        });
        attachedConversationId = initialConversationId;
      } catch (e) {
        // Surface the failure but don't block closing the modal
        // eslint-disable-next-line no-console
        console.error('[CreateSpaceModal] failed to attach conversation to new space', e);
      }
    }
    // Forward the attached conversation id so callers can stitch it into the optimistic
    // SpaceStore update — the create-space response itself doesn't echo back sources.
    onCreated?.(space, selectedEmoji?.id, attachedConversationId);
    handleClose();
  }, [onCreated, handleClose, selectedEmoji, initialConversationId, addConversation]);
  const {
    mutate: createSpace,
    isSubmitting
  } = useCreateSpace({
    cloudId,
    product: assistanceServiceParams?.product ?? 'rovo',
    experienceId: assistanceServiceParams?.experienceId ?? 'ai-mate',
    onSuccess: handleCreated
  });
  const handleFormSubmit = useCallback((values: CreateSpaceFormValues) => {
    const trimmed = values.name.trim();
    if (!trimmed) {
      return;
    }

    // Strip client-only `displayText` before handing to BE / consumers (see toSpaceSource).
    const sourcesForSubmission: SpaceSource[] = values.sources.map(toSpaceSource);

    // Fire the local callback (non-BE) if provided
    onCreate?.({
      name: trimmed,
      about: values.about.trim(),
      emoji: selectedEmoji,
      sources: sourcesForSubmission
    });
    // Create the space; `initialConversationId` is attached in `handleCreated`.
    void createSpace({
      name: trimmed,
      description: values.about.trim() || undefined,
      sources: sourcesForSubmission,
      emoji: selectedEmoji?.id
    });
  }, [createSpace, onCreate, selectedEmoji]);
  return <SmartCardProvider>
			<ModalDialog onClose={handleClose} width="medium" testId="create-space-modal">
				<ModalHeader hasCloseButton>
					<Heading size="medium">{formatMessage(messages.createSpaceModalTitle)}</Heading>
				</ModalHeader>
				<ModalBody>
					<Stack space="space.200">
						<Flex direction="column" gap="space.050">
							<Form<CreateSpaceFormValues> onSubmit={handleFormSubmit}>
								{({
                formProps
              }) => <form {...formProps} id={CREATE_SPACE_FORM_ID} name="create-space">
										<Text as="p" aria-hidden="true" size="small" color="color.text.subtle">
											{formatMessage(messages.createSpaceRequiredFieldsHint, {
                    asterisk: () => <RequiredAsterisk />
                  })}
										</Text>
										<Field name="name" label={formatMessage(messages.createSpaceNameLabel)} isRequired defaultValue={initialName ?? ''} validate={value => value && String(value).trim() ? undefined : nameRequiredMessage} helperMessage={formatMessage(messages.createSpaceNameHelperMessage)} component={({
                  fieldProps
                }) => <Popup zIndex={510} isOpen={isEmojiPickerOpen} onClose={() => setEmojiPickerOpen(false)} placement="bottom-start" content={() => <EmojiPicker emojiProvider={resolvedEmojiProvider} onSelection={handleEmojiSelection} />} trigger={triggerProps => <TextField {...fieldProps} elemBeforeInput={<Pressable {...triggerProps} xcss={styles.emojiPickerTrigger} onClick={() => setEmojiPickerOpen(!isEmojiPickerOpen)} testId="create-space-emoji-picker-trigger">
																	{selectedEmoji ? <ResourcedEmoji emojiId={selectedEmoji} emojiProvider={resolvedEmojiProvider} showTooltip fitToHeight={20} /> : <EmojiAddIcon label={formatMessage(messages.createSpaceEmojiPickerLabel)} />}
																</Pressable>} />} />} />
										<Field<string, HTMLTextAreaElement> name="about" label={formatMessage(messages.createSpaceAboutLabel)} defaultValue="" component={({
                  fieldProps
                }) => <TextArea {...fieldProps} />} />
										<Field name="sources" label={formatMessage(messages.createSpaceSourcesLabel)} defaultValue={initialSources ?? []}>
											{({
                    fieldProps
                  }) => <SpaceSourcesFieldBody fieldProps={fieldProps} formatMessage={formatMessage} plugins={plugins} assistanceServiceParams={assistanceServiceParams} />}
										</Field>
									</form>}
							</Form>
						</Flex>
					</Stack>
				</ModalBody>
				<ModalFooter>
					<Button appearance="subtle" onClick={handleClose}>
						{formatMessage(messages.createSpaceCancel)}
					</Button>
					<Button appearance="primary" type="submit" form={CREATE_SPACE_FORM_ID} testId="create-space-submit" isDisabled={isSubmitting} isLoading={isSubmitting}>
						{formatMessage(messages.createSpaceSubmit)}
					</Button>
				</ModalFooter>
			</ModalDialog>
		</SmartCardProvider>;
};
export const CreateSpaceModal = ({
  isOpen,
  ...rest
}: CreateSpaceModalProps): React.JSX.Element | null => {
  if (!isOpen) {
    return null;
  }
  return <CreateSpaceModalContent {...rest} />;
};